import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cable, Save, Loader2, Percent, RotateCcw, FileDown, Eye, EyeOff, TrendingUp, GripVertical, Minimize2, Maximize2 } from "lucide-react";
import { toast } from "sonner";

const num = (v) => Number(v) || 0;
const fmt = (v) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num(v));
const stok = (v) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(num(v));
const D_KEYS = ["cost_discount", "sale_discount", "electrician_discount", "electrician_nobill_discount"];

const COLS = [
  { key: "liste", label: "Liste", tag: "₺", head: "bg-slate-100 text-slate-600", cell: "text-slate-500", calc: (b) => b, kdv: false },
  { key: "maliyet", label: "Maliyet", tag: "KDV Dahil", head: "bg-indigo-50 text-indigo-700", cell: "text-indigo-700", calc: (b, d) => b * (1 - num(d.cost_discount) / 100), kdv: true },
  { key: "genel", label: "Gen. Satış", tag: "KDV Dahil", head: "bg-sky-50 text-sky-700", cell: "text-sky-700 font-bold", calc: (b, d) => b * (1 - num(d.sale_discount) / 100), kdv: true },
  { key: "elk_satis", label: "Elk. Satış", tag: "KDV Dahil", head: "bg-emerald-50 text-emerald-700", cell: "text-emerald-700 font-bold", calc: (b, d) => b * (1 - num(d.electrician_discount) / 100), kdv: true },
  { key: "elk_net", label: "Elk. Net", tag: "KDV'siz", head: "bg-amber-50 text-amber-700", cell: "text-amber-700 font-bold", calc: (b, d) => b * (1 - num(d.electrician_nobill_discount) / 100), kdv: false },
];
// PDF için seçilebilen tek fiyat (müşteri listesi)
const PDF_MODES = [
  { key: "genel", label: "Genel Satış", tag: "KDV Dahil", calc: (b, d) => b * (1 - num(d.sale_discount) / 100), kdv: true },
  { key: "elk_satis", label: "Elektrikçi Satış", tag: "KDV Dahil", calc: (b, d) => b * (1 - num(d.electrician_discount) / 100), kdv: true },
  { key: "elk_net", label: "Elektrikçi Net", tag: "KDV'siz", calc: (b, d) => b * (1 - num(d.electrician_nobill_discount) / 100), kdv: false },
  { key: "maliyet", label: "Maliyet", tag: "KDV Dahil", calc: (b, d) => b * (1 - num(d.cost_discount) / 100), kdv: true },
  { key: "liste", label: "Liste (Net)", tag: "₺", calc: (b) => b, kdv: false },
];
const DISC_FIELDS = [
  { key: "cost_discount", label: "Maliyet" },
  { key: "sale_discount", label: "Gen. Satış" },
  { key: "electrician_discount", label: "Elk. Satış" },
  { key: "electrician_nobill_discount", label: "Elk. Net" },
];

export default function KabloBulten() {
  const [groups, setGroups] = useState([]);
  const [edit, setEdit] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(() => new Set());
  const [showMargin, setShowMargin] = useState(false);
  const [compact, setCompact] = useState(false);
  const [pdfMode, setPdfMode] = useState("genel");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [dragGroup, setDragGroup] = useState(null);
  const [overGroup, setOverGroup] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cable-bulletin");
      setGroups(data.groups || []);
      const e = {};
      (data.groups || []).forEach((g) => { e[g.group] = Object.fromEntries(D_KEYS.map((k) => [k, g[k] ?? 0])); });
      setEdit(e);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isDirty = (g) => { const s = groups.find((x) => x.group === g); return !!s && D_KEYS.some((k) => num(edit[g]?.[k]) !== num(s[k])); };
  const dirty = useMemo(() => groups.filter((g) => isDirty(g.group)).map((g) => g.group), [groups, edit]); // eslint-disable-line
  const setD = (g, k, v) => setEdit((p) => ({ ...p, [g]: { ...p[g], [k]: v } }));
  const resetAll = () => { const e = {}; groups.forEach((g) => { e[g.group] = Object.fromEntries(D_KEYS.map((k) => [k, g[k] ?? 0])); }); setEdit(e); };
  const toggleGroup = (g) => setHidden((s) => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); return n; });

  const persistOrder = async (order) => { try { await api.put("/cable-bulletin/order", { order }); } catch (e) { /* sessiz */ } };
  const onDrop = (target) => {
    setOverGroup(null);
    const src = dragGroup; setDragGroup(null);
    if (!src || src === target) return;
    setGroups((list) => {
      const arr = [...list];
      const from = arr.findIndex((x) => x.group === src);
      const to = arr.findIndex((x) => x.group === target);
      if (from < 0 || to < 0) return list;
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      persistOrder(arr.map((x) => x.group));
      toast.success("Grup sırası kaydedildi");
      return arr;
    });
  };

  const saveAll = async () => {
    setSaving(true); let n = 0;
    try {
      for (const g of dirty) {
        const v = edit[g] || {};
        const { data } = await api.put("/discounts/global", { group: g, ...Object.fromEntries(D_KEYS.map((k) => [k, num(v[k])])) });
        n += data.updated || 0;
      }
      setGroups((list) => list.map((x) => (dirty.includes(x.group) ? { ...x, ...Object.fromEntries(D_KEYS.map((k) => [k, num(edit[x.group]?.[k])])) } : x)));
      toast.success(`${dirty.length} grup iskontosu kaydedildi · ${n} ürün güncellendi · İskontolar sayfasına da yansıdı`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const visible = groups.filter((g) => !hidden.has(g.group));

  const onPdf = async () => {
    setPdfBusy(true);
    try {
      let copper = null;
      try { const { data } = await api.get("/cable-bulletin/copper"); copper = data; }
      catch (e) { toast.warning("Bakır kuru alınamadı, PDF kursuz oluşturuluyor"); }
      const c = PDF_MODES.find((m) => m.key === pdfMode) || PDF_MODES[0];
      const pdfGroups = visible.map((g) => {
        const d = edit[g.group] || {};
        return { group: g.group, rows: g.items.map((it) => {
          const base = num(it.price) || num(it.list_price);
          const kdvMul = 1 + num(it.kdv_rate || 20) / 100;
          return { cross: it.cross || it.name, price: c.calc(base, d) * (c.kdv ? kdvMul : 1) };
        }) };
      });
      if (!pdfGroups.length) { toast.error("Gösterilecek grup yok"); return; }
      const { buildCableBulletinPdf } = await import("@/lib/cableBulletinPdf");
      await buildCableBulletinPdf({ groups: pdfGroups, priceLabel: c.label, priceTag: c.tag, date: today, copper });
      toast.success("Kablo fiyat listesi PDF indirildi");
    } catch (e) { toast.error("PDF oluşturulamadı: " + (e?.message || "")); }
    finally { setPdfBusy(false); }
  };

  const today = new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
  const totalItems = visible.reduce((s, g) => s + g.items.length, 0);

  // Kompakt / normal görünüm sınıfları
  const masonryCls = compact
    ? "columns-1 sm:columns-2 xl:columns-3 2xl:columns-4 gap-3 [column-fill:balance]"
    : "columns-1 md:columns-2 2xl:columns-3 gap-4 [column-fill:balance]";
  const tblText = compact ? "text-[9.5px]" : "text-[11.5px]";
  const headText = compact ? "text-[6.5px]" : "text-[8.5px]";
  const tagText = compact ? "text-[6px]" : "text-[7.5px]";
  const cellPad = compact ? "py-[1.5px]" : "py-[3px]";
  const cardMb = compact ? "mb-3" : "mb-4";

  return (
    <div className="space-y-4 stagger-in" data-testid="cable-bulletin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeader title="Kablo Bülten" badge={today} subtitle={`Tüm kablo cinsleri ve kesitleri tek ekranda · ${totalItems} kesit`} />
        <div className="flex items-center gap-2 print:hidden">
          <div className="flex items-center gap-1.5 rounded-lg border bg-card px-2 h-9">
            <FileDown className="h-4 w-4 text-muted-foreground" />
            <Select value={pdfMode} onValueChange={setPdfMode}>
              <SelectTrigger className="h-7 border-0 shadow-none px-1 w-[168px] text-xs font-semibold" data-testid="cable-pdf-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{PDF_MODES.map((m) => <SelectItem key={m.key} value={m.key} className="text-xs">{m.label} <span className="opacity-60">· {m.tag}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button size="sm" className="h-9 bg-[#0B192C] hover:bg-[#1a3557]" onClick={onPdf} disabled={pdfBusy} data-testid="cable-pdf">{pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}PDF İndir</Button>
          {dirty.length > 0 && <>
            <Button size="sm" variant="ghost" className="h-9 text-muted-foreground" onClick={resetAll} data-testid="cable-reset-all"><RotateCcw className="h-4 w-4 mr-1.5" />Geri Al</Button>
            <Button size="sm" className="h-9 bg-[#EA5820] hover:bg-[#c2410c]" onClick={saveAll} disabled={saving} data-testid="cable-save-all">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Kaydet ({dirty.length})</Button>
          </>}
        </div>
      </div>

      {/* Grup göster/gizle (sürükle-bırak ile sırala) + kompakt + kâr marjı */}
      <div className="flex flex-wrap items-center gap-2 print:hidden" data-testid="cable-toolbar">
        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mr-0.5">Gruplar</span>
        {groups.map((g) => {
          const on = !hidden.has(g.group);
          const isOver = overGroup === g.group && dragGroup && dragGroup !== g.group;
          return (
            <div key={g.group} draggable
              onDragStart={() => setDragGroup(g.group)}
              onDragEnd={() => { setDragGroup(null); setOverGroup(null); }}
              onDragOver={(e) => { e.preventDefault(); if (overGroup !== g.group) setOverGroup(g.group); }}
              onDrop={(e) => { e.preventDefault(); onDrop(g.group); }}
              data-testid={`group-chip-${g.group}`}
              className={`inline-flex items-center gap-1 rounded-full border pl-1.5 pr-3 py-1 text-xs font-bold transition-all cursor-grab active:cursor-grabbing select-none
                ${on ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-background text-muted-foreground opacity-60 hover:opacity-100"}
                ${dragGroup === g.group ? "opacity-40 scale-95" : ""}
                ${isOver ? "ring-2 ring-[#EA5820] ring-offset-1" : ""}`}>
              <GripVertical className={`h-3.5 w-3.5 ${on ? "text-slate-400" : "text-muted-foreground"}`} />
              <button type="button" onClick={() => toggleGroup(g.group)} className="inline-flex items-center gap-1" data-testid={`group-toggle-${g.group}`}>
                {on ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{g.group}
              </button>
            </div>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => setCompact((v) => !v)} data-testid="toggle-compact"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold transition-all ${compact ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-background text-muted-foreground hover:bg-muted"}`}>
            {compact ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}{compact ? "Normal Görünüm" : "Kompakt Görünüm"}
          </button>
          <button type="button" onClick={() => setShowMargin((v) => !v)} data-testid="toggle-margin"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold transition-all ${showMargin ? "bg-fuchsia-600 text-white border-fuchsia-600" : "bg-background text-muted-foreground hover:bg-muted"}`}>
            <TrendingUp className="h-3.5 w-3.5" />Kâr Marjı {showMargin ? "Açık" : "Kapalı"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border rounded-xl"><Cable className="h-10 w-10 mx-auto mb-3 opacity-40" />Görüntülenecek kablo grubu yok (hepsi gizli veya kablo ürünü yok).</div>
      ) : (
        <>
          {/* Masonry: gruplar boşluk bırakmadan dikey dolar → hepsi tek ekranda mantıklı dizilim */}
          <div className={masonryCls} data-testid="cable-masonry">
            {visible.map((g) => {
              const d = edit[g.group] || {};
              return (
                <div key={g.group} className={`${cardMb} break-inside-avoid rounded-xl border border-slate-300 overflow-hidden shadow-sm bg-white`} data-testid={`cable-group-${g.group}`}>
                  <div className="flex items-center justify-between bg-gradient-to-r from-[#0B192C] to-[#1a3557] text-white px-3 py-1.5">
                    <span className={`font-head font-black tracking-tight truncate flex items-center gap-2 ${compact ? "text-[12px]" : "text-[14px]"}`}><Cable className="h-4 w-4 text-[#EA5820]" />{g.group}</span>
                    <span className="text-[10px] font-semibold text-slate-300 whitespace-nowrap">{g.items.length} kesit</span>
                  </div>
                  <table className={`w-full ${tblText} tabular-nums border-collapse`}>
                    <thead>
                      <tr className={`${headText} font-bold uppercase tracking-tight text-slate-500`}>
                        <th className="px-1.5 py-1 text-left bg-slate-50">Kesit</th>
                        <th className="px-1 py-1 text-right bg-slate-50">Stok</th>
                        {COLS.map((c) => <th key={c.key} className={`px-1.5 py-1 text-right leading-tight ${c.head}`}>{c.label}<span className={`block font-normal opacity-80 ${tagText}`}>{c.tag}</span></th>)}
                        {showMargin && <th className="px-1.5 py-1 text-right leading-tight bg-fuchsia-50 text-fuchsia-700">Marj<span className={`block font-normal opacity-80 ${tagText}`}>%</span></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((it, i) => {
                        const base = num(it.price) || num(it.list_price);
                        const kdvMul = 1 + num(it.kdv_rate || 20) / 100;
                        const oos = num(it.quantity) <= 0;
                        const costNet = base * (1 - num(d.cost_discount) / 100);
                        const saleNet = base * (1 - num(d.electrician_discount) / 100);
                        const marj = saleNet > 0 ? ((saleNet - costNet) / saleNet) * 100 : 0;
                        return (
                          <tr key={it.id} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50/60" : "bg-white"} hover:bg-[#EA5820]/[0.06]`} data-testid={`cable-row-${it.id}`}>
                            <td className={`px-1.5 ${cellPad} text-left font-bold text-slate-800 whitespace-nowrap`}><span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle ${oos ? "bg-slate-300" : "bg-emerald-500"}`} />{it.cross || it.name}</td>
                            <td className={`px-1 ${cellPad} text-right ${oos ? "text-slate-300" : "text-slate-500"}`} data-testid={`cable-stock-${it.id}`}>{stok(it.quantity)}</td>
                            {COLS.map((c) => <td key={c.key} className={`px-1.5 ${cellPad} text-right whitespace-nowrap border-l border-slate-100 ${c.cell}`} data-testid={`cable-${c.key}-${it.id}`}>{fmt(c.calc(base, d) * (c.kdv ? kdvMul : 1))}</td>)}
                            {showMargin && <td className={`px-1.5 ${cellPad} text-right whitespace-nowrap border-l border-slate-100 font-bold ${marj >= 0 ? "text-fuchsia-700" : "text-red-600"}`} data-testid={`cable-marj-${it.id}`}>%{fmt(marj)}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {/* İSKONTO paneli — grup bazlı, canlı; kaydedince genel iskontoya yansır */}
          <div className="rounded-xl border-2 border-slate-300 overflow-hidden print:hidden" data-testid="cable-discount-panel">
            <div className="bg-slate-800 text-white px-3 py-2 flex items-center gap-2">
              <Percent className="h-4 w-4 text-[#EA5820]" />
              <span className="font-head font-black text-sm uppercase tracking-wider">İskonto Ayarları</span>
              <span className="text-[11px] text-slate-300 hidden sm:inline">· Değiştirince fiyatlar anlık güncellenir; Kaydet ile kalıcı olur ve İskontolar sayfasına yansır</span>
              {dirty.length > 0 && <Button size="sm" className="h-7 ml-auto bg-[#EA5820] hover:bg-[#c2410c]" onClick={saveAll} disabled={saving} data-testid="cable-disc-save">{saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}Kaydet ({dirty.length})</Button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                    <th className="px-3 py-2 text-left">Kablo Grubu</th>
                    {DISC_FIELDS.map((f) => <th key={f.key} className="px-2 py-2 text-center w-28">{f.label} %</th>)}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const d = edit[g.group] || {};
                    const gd = isDirty(g.group);
                    return (
                      <tr key={g.group} className={`border-t ${gd ? "bg-amber-50" : ""} ${hidden.has(g.group) ? "opacity-50" : ""}`} data-testid={`cable-disc-row-${g.group}`}>
                        <td className="px-3 py-1.5 font-bold text-slate-700">{g.group} <span className="text-[11px] font-normal text-muted-foreground">({g.items.length})</span></td>
                        {DISC_FIELDS.map((f) => (
                          <td key={f.key} className="px-2 py-1.5 text-center">
                            <Input type="number" value={d[f.key] ?? 0} onChange={(e) => setD(g.group, f.key, e.target.value)} className="h-8 w-20 mx-auto text-center tabular-nums font-mono" data-testid={`cable-disc-${g.group}-${f.key}`} />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
